package com.havn.app.features.settings.data

import com.havn.app.core.logging.HavnLogger
import com.havn.app.core.supabase.HavnSupabaseConfig
import com.havn.app.features.settings.domain.McpApiKey
import com.havn.app.features.settings.domain.McpApiKeyRepository
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

class SupabaseMcpApiKeyRepository(
    private val client: SupabaseClient
) : McpApiKeyRepository {
    override suspend fun listKeys(agencyId: String?): Result<List<McpApiKey>> = runCatching {
        client.postgrest.rpc(
            function = "havn_list_mcp_api_keys",
            parameters = McpApiKeyListParams(agencyId = agencyId)
        ).decodeList<McpApiKeyRow>().map { it.toDomain() }
    }.onFailure {
        HavnLogger.error("MCP", "Failed to list MCP API keys", it)
    }

    override suspend fun createOrRotateKey(agencyId: String?, name: String): Result<McpApiKey> = runCatching {
        client.postgrest.rpc(
            function = "havn_create_or_rotate_mcp_api_key",
            parameters = McpApiKeyCreateParams(
                agencyId = agencyId,
                name = name,
                environment = HavnSupabaseConfig.environment
            )
        ).decodeSingle<McpApiKeyRow>().toDomain()
    }.onFailure {
        HavnLogger.error("MCP", "Failed to create or rotate MCP API key", it)
    }

    override suspend fun revokeKey(keyId: String): Result<Unit> = runCatching {
        client.postgrest.rpc(
            function = "havn_revoke_mcp_api_key",
            parameters = McpApiKeyRevokeParams(keyId = keyId)
        )
        Unit
    }.onFailure {
        HavnLogger.error("MCP", "Failed to revoke MCP API key $keyId", it)
    }
}

@Serializable
private data class McpApiKeyListParams(
    @SerialName("p_agency_id") val agencyId: String?
)

@Serializable
private data class McpApiKeyCreateParams(
    @SerialName("p_agency_id") val agencyId: String?,
    @SerialName("p_name") val name: String,
    @SerialName("p_environment") val environment: String
)

@Serializable
private data class McpApiKeyRevokeParams(
    @SerialName("p_key_id") val keyId: String
)

@Serializable
private data class McpApiKeyRow(
    val id: String,
    @SerialName("api_key") val apiKey: String? = null,
    @SerialName("owner_agent_id") val ownerAgentId: String,
    @SerialName("agency_id") val agencyId: String? = null,
    val name: String,
    @SerialName("key_prefix") val keyPrefix: String,
    val scopes: List<String> = emptyList(),
    @SerialName("created_at") val createdAt: String,
    @SerialName("last_used_at") val lastUsedAt: String? = null,
    @SerialName("revoked_at") val revokedAt: String? = null
) {
    fun toDomain(): McpApiKey = McpApiKey(
        id = id,
        ownerAgentId = ownerAgentId,
        agencyId = agencyId,
        name = name,
        keyPrefix = keyPrefix,
        scopes = scopes,
        createdAt = createdAt,
        lastUsedAt = lastUsedAt,
        revokedAt = revokedAt,
        apiKey = apiKey
    )
}
